import React, { useState, useRef, useEffect } from "react";
import { useDismiss } from "@jkast90/kast-common/ui";
import { createPortal } from "react-dom";
import styles from "./MentionInput.module.css";

interface Profile {
  id: number;
  first_name: string;
  last_name: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentionedUserIds?: number[]) => void;
  profiles: Profile[];
  placeholder?: string;
  rows?: number;
}

export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  profiles,
  placeholder = "Type @ to mention someone...",
  rows = 4,
}) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionPosition, setMentionPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter profiles based on search
  const filteredProfiles = profiles.filter((p) => {
    const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
    const search = mentionSearch.toLowerCase();
    return (
      fullName.includes(search) || p.first_name.toLowerCase().includes(search)
    );
  });

  // Extract mentioned user IDs from text
  const extractMentionedUserIds = (text: string): number[] => {
    const mentionPattern = /@(\w+)/g;
    const matches = text.matchAll(mentionPattern);
    const userIds: number[] = [];

    for (const match of matches) {
      const firstName = match[1];
      const profile = profiles.find(
        (p) => p.first_name.toLowerCase() === firstName.toLowerCase(),
      );
      if (profile && !userIds.includes(profile.id)) {
        userIds.push(profile.id);
      }
    }

    return userIds;
  };

  // Handle text change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const mentionedUserIds = extractMentionedUserIds(newValue);
    onChange(newValue, mentionedUserIds);

    // Check if user just typed @
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = newValue.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      // Only show mentions if @ is at start or after whitespace, and no space after @
      const isAtStart = lastAtIndex === 0;
      const charBeforeAt = isAtStart ? "" : textBeforeCursor[lastAtIndex - 1];
      const isValidPosition =
        isAtStart || charBeforeAt === " " || charBeforeAt === "\n";

      if (isValidPosition && !textAfterAt.includes(" ")) {
        setMentionSearch(textAfterAt);
        setShowMentions(true);
        setSelectedIndex(0);

        // Calculate position for dropdown (fixed positioning relative to viewport)
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          const rect = textarea.getBoundingClientRect();

          // Get caret coordinates within the textarea
          const caretCoords = getCaretCoordinates(textarea, cursorPosition);

          // Convert to viewport coordinates for fixed positioning
          const position = {
            top: rect.top + caretCoords.top,
            left: rect.left + caretCoords.left,
          };
          setMentionPosition(position);
        }
        return;
      }
    }

    setShowMentions(false);
  };

  // Handle mention selection
  const selectMention = (profile: Profile) => {
    if (!textareaRef.current) return;

    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const beforeAt = value.slice(0, lastAtIndex);
      const mentionText = `@${profile.first_name}`;
      const newValue = beforeAt + mentionText + " " + textAfterCursor;

      // Extract mentioned user IDs including the newly selected profile
      const mentionedUserIds = extractMentionedUserIds(newValue);

      // Call onChange with both the new value and the mentioned user IDs
      onChange(newValue, mentionedUserIds);

      // Set cursor position after the mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newPosition = lastAtIndex + mentionText.length + 1;
          textareaRef.current.setSelectionRange(newPosition, newPosition);
          textareaRef.current.focus();
        }
      }, 0);
    }

    setShowMentions(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentions || filteredProfiles.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredProfiles.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(
        (prev) =>
          (prev - 1 + filteredProfiles.length) % filteredProfiles.length,
      );
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (showMentions) {
        e.preventDefault();
        selectMention(filteredProfiles[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowMentions(false);
    }
  };

  // Close dropdown when clicking outside
  // (Escape is handled by the textarea's key handler above.)
  useDismiss(
    showMentions,
    [dropdownRef, textareaRef],
    () => setShowMentions(false),
    { escape: false },
  );

  // Scroll selected item into view
  useEffect(() => {
    if (dropdownRef.current && showMentions) {
      const selectedElement = dropdownRef.current.children[
        selectedIndex
      ] as HTMLElement;
      selectedElement?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, showMentions]);

  return (
    <>
      <div className={styles.container}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          className={styles.textarea}
        />
      </div>

      {showMentions &&
        filteredProfiles.length > 0 &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={
              mentionPosition
                ? { top: mentionPosition.top, left: mentionPosition.left }
                : {}
            }
          >
            {filteredProfiles.map((profile, index) => (
              <div
                key={profile.id}
                className={`${styles.mentionItem} ${index === selectedIndex ? styles.selected : ""}`}
                onClick={() => selectMention(profile)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span className={styles.mentionName}>
                  {profile.first_name} {profile.last_name}
                </span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

// Helper function to get caret coordinates relative to textarea
function getCaretCoordinates(
  element: HTMLTextAreaElement,
  position: number,
): { top: number; left: number } {
  const div = document.createElement("div");
  const style = getComputedStyle(element);

  // Copy relevant styles from textarea to div
  // CSS property names: getPropertyValue/setProperty take the hyphenated form.
  const properties = [
    "font-family",
    "font-size",
    "font-weight",
    "font-style",
    "letter-spacing",
    "line-height",
    "padding",
    "padding-top",
    "padding-right",
    "padding-bottom",
    "padding-left",
    "border-width",
    "box-sizing",
    "word-wrap",
    "white-space",
  ];

  properties.forEach((prop) => {
    div.style.setProperty(prop, style.getPropertyValue(prop));
  });

  // Position offscreen but visible for measurement
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordWrap = "break-word";
  div.style.top = "0";
  div.style.left = "-9999px";
  div.style.width = element.clientWidth + "px";
  div.style.height = "auto";
  div.style.overflow = "hidden";

  document.body.appendChild(div);

  // Get text up to cursor position
  const text = element.value.substring(0, position);
  div.textContent = text;

  // Create a span at cursor position
  const span = document.createElement("span");
  span.textContent = "|";
  span.style.position = "relative";
  div.appendChild(span);

  // Get span position
  const spanRect = span.getBoundingClientRect();
  const divRect = div.getBoundingClientRect();

  document.body.removeChild(div);

  // Calculate position relative to textarea content area (inside padding)
  const paddingTop = parseFloat(style.paddingTop) || 0;
  const paddingLeft = parseFloat(style.paddingLeft) || 0;

  return {
    top: spanRect.top - divRect.top + paddingTop - element.scrollTop + 20,
    left: spanRect.left - divRect.left + paddingLeft - element.scrollLeft,
  };
}
